import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, ArrowLeft, Loader2, User, Mail, Phone, MapPin, Briefcase, GraduationCap, Lock, Users, Camera } from 'lucide-react';
import SearchableSelect from '../../components/ui/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const MemberRegistrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [options, setOptions] = useState({
    states: [],
    nukhs: [],
    education: [],
    occupation: [],
    gotras: [],
    countries: [],
    statesByCountry: {},
    citiesByCountry: {},
    citiesByState: {}
  });

  const [formData, setFormData] = useState({
    // Login credentials
    email: '',
    password: '',
    confirmPassword: '',
    // Personal Details
    fullName: '',
    fatherOrSpouseName: '',
    dateOfBirth: '',
    phone: '',
    alternateContact: '',
    // Family Details
    familyName: '',
    nukh: '',
    // Address
    address: '',
    correspondenceAddress: '',
    country: 'India',
    state: '',
    city: '',
    pincode: '',
    // Education & Occupation
    educationalQualification: '',
    occupation: '',
    occupationOther: '',
    workplaceAddress: '',
    designation: '',
    // Social
    socialInvolvement: ''
  });

  // Check if country is "Other" (requires manual text input)
  const isOtherCountry = (country) => country === 'Other';

  // Get states based on selected country
  const getStatesForCountry = (country) => {
    if (!country || !options.statesByCountry || isOtherCountry(country)) return [];
    return options.statesByCountry[country] || [];
  };

  // Get cities based on state (for India) or country (for others)
  const getCitiesForState = (country, state) => {
    if (!country || isOtherCountry(country)) return [];
    
    // For India, use state-specific cities
    if (country === 'India' && state && options.citiesByState) {
      return options.citiesByState[state] || [];
    }
    
    // For other countries, use country-level cities
    if (options.citiesByCountry) {
      return options.citiesByCountry[country] || [];
    }
    
    return [];
  };

  // Refresh local city options immediately after a custom city is saved.
  const handleCustomCitySaved = (cityName, ctry, st) => {
    setOptions(prev => {
      if (ctry === 'India' && st) {
        const list = prev.citiesByState?.[st] || [];
        if (list.includes(cityName)) return prev;
        return { ...prev, citiesByState: { ...prev.citiesByState, [st]: [...list, cityName].sort((a, b) => a.localeCompare(b)) } };
      }
      if (ctry) {
        const list = prev.citiesByCountry?.[ctry] || [];
        if (list.includes(cityName)) return prev;
        return { ...prev, citiesByCountry: { ...prev.citiesByCountry, [ctry]: [...list, cityName].sort((a, b) => a.localeCompare(b)) } };
      }
      return prev;
    });
  };


  useEffect(() => {
    fetchOptions();
    fetchLocationOptions();
  }, []);

  // Reset state and city when country changes
  useEffect(() => {
    if (formData.country) {
      setFormData(prev => ({
        ...prev,
        state: '',
        city: ''
      }));
    }
  }, [formData.country]);

  // Reset city when state changes
  useEffect(() => {
    if (formData.state) {
      setFormData(prev => ({
        ...prev,
        city: ''
      }));
    }
  }, [formData.state]);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchLocationOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/professionals/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(prev => ({
          ...prev,
          countries: data.countries || [],
          statesByCountry: data.statesByCountry || {},
          citiesByCountry: data.citiesByCountry || {},
          citiesByState: data.citiesByState || {}
        }));
      }
    } catch (error) {
      console.error('Error fetching location options:', error);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('केवल JPEG, PNG, और WebP इमेज की अनुमति है / Only JPEG, PNG, and WebP images are allowed');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > MAX_FILE_SIZE) {
        toast.error('इमेज का आकार 5MB से कम होना चाहिए / Image size should be less than 5MB');
        return;
      }
      setProfilePhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast.error('पासवर्ड मेल नहीं खाते / Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('पासवर्ड कम से कम 6 अक्षरों का होना चाहिए / Password must be at least 6 characters');
      return;
    }
    if (!formData.fullName || !formData.email || !formData.phone || !formData.address) {
      toast.error('कृपया सभी आवश्यक फ़ील्ड भरें / Please fill all required fields');
      return;
    }
    if (!formData.country || !formData.state || !formData.city) {
      toast.error('कृपया देश, राज्य और शहर चुनें / Please select country, state and city');
      return;
    }

    setLoading(true);

    try {
      // Use FormData if there's a photo, otherwise use JSON
      if (profilePhoto) {
        const formDataToSend = new FormData();
        formDataToSend.append('email', formData.email);
        formDataToSend.append('password', formData.password);
        formDataToSend.append('fullName', formData.fullName);
        formDataToSend.append('familyName', formData.familyName || '');
        formDataToSend.append('nukh', formData.nukh || '');
        formDataToSend.append('country', formData.country || '');
        formDataToSend.append('state', formData.state || '');
        formDataToSend.append('city', formData.city || '');
        formDataToSend.append('pincode', formData.pincode || '');
        formDataToSend.append('fatherOrSpouseName', formData.fatherOrSpouseName || '');
        formDataToSend.append('dateOfBirth', formData.dateOfBirth || '');
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('alternateContact', formData.alternateContact || '');
        formDataToSend.append('address', formData.address);
        formDataToSend.append('correspondenceAddress', formData.correspondenceAddress || '');
        formDataToSend.append('educationalQualification', formData.educationalQualification || '');
        formDataToSend.append('occupation', formData.occupation || '');
        formDataToSend.append('occupationOther', formData.occupationOther || '');
        formDataToSend.append('workplaceAddress', formData.workplaceAddress || '');
        formDataToSend.append('designation', formData.designation || '');
        formDataToSend.append('socialInvolvement', formData.socialInvolvement || '');
        formDataToSend.append('photo', profilePhoto);

        const response = await fetch(`${BACKEND_URL}/api/auth/register-with-photo`, {
          method: 'POST',
          body: formDataToSend
        });

        if (response.ok) {
          setSubmitted(true);
          toast.success('पंजीकरण सफल! / Registration successful!');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'पंजीकरण विफल / Registration failed');
        }
      } else {
        const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            fullName: formData.fullName,
            familyName: formData.familyName || null,
            nukh: formData.nukh || null,
            country: formData.country || null,
            state: formData.state || null,
            city: formData.city || null,
            pincode: formData.pincode || null,
            fatherOrSpouseName: formData.fatherOrSpouseName || null,
            dateOfBirth: formData.dateOfBirth || null,
            phone: formData.phone,
            alternateContact: formData.alternateContact || null,
            address: formData.address,
            correspondenceAddress: formData.correspondenceAddress || null,
            educationalQualification: formData.educationalQualification || null,
            occupation: formData.occupation || null,
            occupationOther: formData.occupationOther || null,
            workplaceAddress: formData.workplaceAddress || null,
            designation: formData.designation || null,
            socialInvolvement: formData.socialInvolvement || null
          })
        });

        if (response.ok) {
          setSubmitted(true);
          toast.success('पंजीकरण सफल! / Registration successful!');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'पंजीकरण विफल / Registration failed');
        }
      }
    } catch (error) {
      toast.error('कुछ गलत हो गया। कृपया पुनः प्रयास करें। / Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Card className="p-12 shadow-lg">
            <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-4" />
            <h1 className="font-bold text-3xl text-primary mb-4">
              पंजीकरण सफल!
            </h1>
            <p className="text-lg text-gray-600 mb-2">Registration Submitted Successfully!</p>
            <p className="text-gray-500 mb-6">
              आपका आवेदन प्रशासक अनुमोदन के लिए प्रस्तुत किया गया है।
              <br />
              Your application has been submitted for admin approval.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6 text-left">
              <p className="font-semibold text-primary mb-3">आगे क्या होगा? / What's Next?</p>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• आपके आवेदन की समीक्षा हमारी प्रशासन टीम द्वारा की जाएगी</li>
                <li>• Your profile will be reviewed by our admin team</li>
                <li>• अनुमोदन के बाद आप अपने खाते में लॉगिन कर सकेंगे</li>
                <li>• After approval, you can login to your account</li>
                <li>• आपको एक अद्वितीय सदस्यता आईडी प्राप्त होगी</li>
                <li>• You will receive a unique Membership ID</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  मुख्य पृष्ठ / Home
                </Button>
              </Link>
              <Link to="/auth">
                <Button className="bg-primary hover:bg-primary/90">
                  लॉगिन करें / Login
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/auth" className="inline-flex items-center text-primary hover:underline mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            वापस जाएं / Back
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
            व्यक्तिगत सदस्यता पंजीकरण
          </h1>
          <p className="text-gray-600 text-lg">Individual Membership Registration</p>
          <p className="text-sm text-gray-500 mt-2">
            ब्रह्मक्षत्रिय मारवाड़ी खत्री एसोसिएशन (महासभा)
          </p>
        </div>

        {/* Info Banner */}
        <Card className="p-4 mb-6 bg-orange-50 border-orange-200">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-700">
              <p className="font-medium text-primary mb-1">पात्रता / Eligibility:</p>
              <p>ब्रह्मक्षत्रिय मारवाड़ी खत्री समाज का कोई भी व्यक्ति जो 18 वर्ष से अधिक आयु का है, व्यक्तिगत सदस्यता के लिये आवेदन कर सकता है।</p>
              <p className="mt-1 text-gray-500">Any person of Brahmkshatriya Marwadi Khatri community above 18 years can apply for individual membership.</p>
            </div>
          </div>
        </Card>

        {/* Registration Form */}
        <Card className="p-6 md:p-8 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Section 1: Login Credentials */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                लॉगिन विवरण / Login Credentials
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="email" className="text-gray-700">
                    ईमेल / Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="mt-1"
                    data-testid="member-reg-email"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-gray-700">
                    पासवर्ड / Password <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="कम से कम 6 अक्षर"
                    className="mt-1"
                    data-testid="member-reg-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword" className="text-gray-700">
                    पासवर्ड पुष्टि / Confirm Password <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="पासवर्ड दोहराएं"
                    className="mt-1"
                    data-testid="member-reg-confirm-password"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Personal Details */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                व्यक्तिगत विवरण / Personal Details
              </h2>
              
              {/* Profile Photo Upload */}
              <div className="flex items-start gap-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-dashed border-orange-300">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-10 h-10 text-gray-400" />
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 shadow-md">
                    <Camera size={16} className="text-white" />
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handlePhotoChange}
                      data-testid="member-reg-photo"
                    />
                  </label>
                </div>
                <div className="flex-1">
                  <Label className="text-base text-gray-700">प्रोफाइल फोटो / Profile Photo</Label>
                  <p className="text-sm text-gray-600 mt-1">
                    एक पेशेवर फोटो अपलोड करें। JPEG, PNG या WebP (अधिकतम 5MB)।
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Upload a professional photo. JPEG, PNG or WebP (max 5MB).
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fullName" className="text-gray-700">
                    पूरा नाम (नख सहित) / Full Name (with Nukh) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    placeholder="उदा: राजेश कुमार भेड़ा"
                    className="mt-1"
                    data-testid="member-reg-fullname"
                  />
                </div>
                <div>
                  <Label htmlFor="fatherOrSpouseName" className="text-gray-700">
                    पिता / पति का नाम / Father's/Spouse's Name
                  </Label>
                  <Input
                    id="fatherOrSpouseName"
                    name="fatherOrSpouseName"
                    value={formData.fatherOrSpouseName}
                    onChange={handleChange}
                    placeholder="पिता या पति का पूरा नाम"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dateOfBirth" className="text-gray-700">
                    जन्म तिथि / Date of Birth
                  </Label>
                  <Input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-gray-700">
                    मोबाइल नंबर / Mobile Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 9876543210"
                    className="mt-1"
                    data-testid="member-reg-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="alternateContact" className="text-gray-700">
                    वैकल्पिक संपर्क / Alternate Contact
                  </Label>
                  <Input
                    id="alternateContact"
                    name="alternateContact"
                    value={formData.alternateContact}
                    onChange={handleChange}
                    placeholder="वैकल्पिक फोन या ईमेल"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Family Details */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                पारिवारिक विवरण / Family Details
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="familyName" className="text-gray-700">
                    परिवार का नाम / Family Name
                  </Label>
                  <Input
                    id="familyName"
                    name="familyName"
                    value={formData.familyName}
                    onChange={handleChange}
                    placeholder="परिवार का नाम"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="nukh" className="text-gray-700">
                    नुख / Nukh
                  </Label>
                  <SearchableSelect
                    options={options.nukhs || []}
                    value={formData.nukh}
                    onChange={(value) => setFormData(prev => ({ ...prev, nukh: value }))}
                    placeholder="नुख खोजें / Type to search nukh..."
                    filterMode="startsWith"
                    allowCustom={false}
                    name="nukh"
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Address */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                पता विवरण / Address Details
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="address" className="text-gray-700">
                    स्थायी पता / Permanent Address <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    placeholder="पूरा स्थायी पता"
                    className="mt-1"
                    rows={2}
                    data-testid="member-reg-address"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="correspondenceAddress" className="text-gray-700">
                    वर्तमान पत्राचार पता / Current Correspondence Address
                  </Label>
                  <Textarea
                    id="correspondenceAddress"
                    name="correspondenceAddress"
                    value={formData.correspondenceAddress}
                    onChange={handleChange}
                    placeholder="यदि स्थायी पते से भिन्न हो"
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="country" className="text-gray-700">
                    देश / Country <span className="text-red-500">*</span>
                  </Label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="member-reg-country"
                  >
                    <option value="">देश चुनें / Select Country</option>
                    {options.countries?.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="state" className="text-gray-700">
                    राज्य / State <span className="text-red-500">*</span>
                  </Label>
                  {isOtherCountry(formData.country) ? (
                    <Input
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="राज्य दर्ज करें / Enter state name"
                      required
                      className="mt-1"
                      data-testid="member-reg-state"
                    />
                  ) : (
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      disabled={!formData.country}
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50"
                      data-testid="member-reg-state"
                    >
                      <option value="">राज्य चुनें / Select State</option>
                      {getStatesForCountry(formData.country).map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <Label htmlFor="city" className="text-gray-700">
                    शहर / City <span className="text-red-500">*</span>
                  </Label>
                  {isOtherCountry(formData.country) ? (
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="शहर दर्ज करें / Enter city name"
                      required
                      className="mt-1"
                      data-testid="member-reg-city"
                    />
                  ) : (
                    <SearchableSelect
                      options={getCitiesForState(formData.country, formData.state)}
                      value={formData.city}
                      onChange={(value) => setFormData(prev => ({ ...prev, city: value }))}
                      placeholder="शहर खोजें / Search city..."
                      allowCustom={true}
                      disabled={!formData.state}
                      name="city"
                      required
                      data-testid="member-reg-city"
                      saveCustomCity={true}
                      country={formData.country}
                      state={formData.state}
                      onCustomCitySaved={handleCustomCitySaved}
                    />
                  )}
                </div>
                <div>
                  <Label htmlFor="pincode" className="text-gray-700">
                    पिनकोड / Pincode
                  </Label>
                  <Input
                    id="pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder="पिनकोड दर्ज करें"
                    className="mt-1"
                    data-testid="member-reg-pincode"
                  />
                </div>
              </div>
            </div>

            {/* Section 5: Education & Occupation */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                शिक्षा एवं व्यवसाय / Education & Occupation
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="educationalQualification" className="text-gray-700">
                    शैक्षिक योग्यता / Educational Qualification
                  </Label>
                  <select
                    id="educationalQualification"
                    name="educationalQualification"
                    value={formData.educationalQualification}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">योग्यता चुनें / Select Qualification</option>
                    {options.education?.map((e) => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="occupation" className="text-gray-700">
                    व्यवसाय / पेशा / Occupation
                  </Label>
                  <select
                    id="occupation"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    <option value="">व्यवसाय चुनें / Select Occupation</option>
                    {options.occupation?.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                </div>
                {formData.occupation === 'अन्य (Other)' && (
                  <div>
                    <Label htmlFor="occupationOther" className="text-gray-700">
                      अन्य व्यवसाय विवरण / Other Occupation Details
                    </Label>
                    <Input
                      id="occupationOther"
                      name="occupationOther"
                      value={formData.occupationOther}
                      onChange={handleChange}
                      placeholder="अन्य व्यवसाय का विवरण"
                      className="mt-1"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="designation" className="text-gray-700">
                    पदनाम / Designation
                  </Label>
                  <Input
                    id="designation"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    placeholder="कार्य का पदनाम"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="workplaceAddress" className="text-gray-700">
                    कार्यालय / व्यवसाय स्थल का पता / Workplace Address
                  </Label>
                  <Input
                    id="workplaceAddress"
                    name="workplaceAddress"
                    value={formData.workplaceAddress}
                    onChange={handleChange}
                    placeholder="कार्यालय का नाम व पता"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Section 6: Social Involvement */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                सामाजिक सहभागिता / Social Involvement
              </h2>
              <div>
                <Label htmlFor="socialInvolvement" className="text-gray-700">
                  समाजिक / सांस्कृतिक सहभागिता का विवरण
                </Label>
                <Textarea
                  id="socialInvolvement"
                  name="socialInvolvement"
                  value={formData.socialInvolvement}
                  onChange={handleChange}
                  placeholder="किसी सामाजिक या सांस्कृतिक संगठन में सहभागिता का विवरण दें"
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4 border-t border-gray-200">
              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold text-lg"
                disabled={loading}
                data-testid="member-reg-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    प्रस्तुत हो रहा है... / Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    पंजीकरण प्रस्तुत करें / Submit Registration
                  </>
                )}
              </Button>
              <p className="text-center text-sm text-gray-500 mt-4">
                पहले से सदस्य हैं?{' '}
                <Link to="/auth" className="text-primary hover:underline font-medium">
                  लॉगिन करें / Login
                </Link>
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default MemberRegistrationPage;
